import { forwardRef, useState, type ComponentProps } from 'react';

import { Input } from '@/components/ui/input';

import { formatDecimal, isDecimalDraft, parseDecimal } from 'helpers/decimal';

export type DecimalInputProps = Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'step'> & {
    value: number | null;
    onChange: (value: number | null) => void;
};

/**
 * Champ de saisie d'un nombre à décimales libres (prix, température…) — voir `helpers/decimal`.
 *
 * Tant que le champ a le focus, c'est le TEXTE saisi qui s'affiche : sans ça « 1,0 » redeviendrait « 1 »
 * sous les doigts, et on ne pourrait pas taper « 1,05 ». Au blur, le brouillon est lâché et le champ
 * réaffiche le nombre retenu par le formulaire : la saisie et sa lecture se rejoignent sous les yeux.
 */
export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(
    ({ value, onChange, onBlur, ...props }, ref) => {
        const [draft, setDraft] = useState<string | null>(null);

        return (
            <Input
                ref={ref}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={draft ?? formatDecimal(value)}
                onChange={(event) => {
                    const text = event.target.value;
                    if (!isDecimalDraft(text)) {
                        return;
                    }
                    setDraft(text);
                    onChange(parseDecimal(text));
                }}
                onBlur={(event) => {
                    setDraft(null);
                    onBlur?.(event);
                }}
                {...props}
            />
        );
    },
);

DecimalInput.displayName = 'DecimalInput';
